// Dagstart — het ochtendoverzicht waar de planner met het team doorheen loopt.
//
// Opvolger van "Mijn Week". Wat er verandert en waarom:
//   · lanes op urgentie i.p.v. groepen op PrioNr — de dagstart gaat over wat is blijven
//     liggen, niet over wat ooit belangrijk werd gevonden
//   · achterstallig en ongepland komen mee, ook al vallen ze buiten het venster
//   · de capaciteit komt uit capacityCalc (net als /capaciteit), niet uit een eigen
//     greedy berekening met een vaste 8 uur — dezelfde persoon gaf twee cijfers
//   · alles dichter op elkaar: het eerste taakblok stond op ~630px, nu binnen ~200px

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  Checkbox, DialogTitle, IconButton, LinearProgress, MenuItem, Select, Stack, Tab, Tabs, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  DensityMedium as DensityMediumIcon,
  DensitySmall as DensitySmallIcon,
  InfoOutlined as InfoOutlinedIcon,
  KeyboardOutlined as KeyboardOutlinedIcon,
  Refresh as RefreshIcon,
  Group as GroupIcon,
  Slideshow as SlideshowIcon,
} from '@mui/icons-material';

import PageHeader from '../../ui-components/PageHeader';
import AfgerondSignaal from '../../ui-components/AfgerondSignaal';
import CapaciteitsRegels from '../../ui-components/CapaciteitsRegels';
import { useCapacityRules } from '../../utils/useCapacityRules';
import { verschuifWerkdagen, werkdagenInBereik } from '../../utils/dagstartLanes';
import { formatDateISO, parseISODate, addDays } from '../../utils/weekUtils';
import { biedOngedaanAan } from '../../utils/ongedaan';
import BulkNaarVandaagDialog, { naarVandaag } from './BulkNaarVandaagDialog';
import PersonTimeline from './PersonTimeline';
import TaskDetailDrawer from './TaskDetailDrawer';
import { DICHTHEDEN } from './dichtheden';
import { useDagstartData } from './useDagstartData';
import { useKlantAfkortingen } from '../../utils/klantAfkortingen';
import toast from 'react-hot-toast';
import { useMsal } from '@azure/msal-react';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { useUserRole } from '../../contexts/UserRoleContext';
import { usePriveTaken, isVerborgenVoor } from '../../utils/priveTaken';
import { DISCIPLINES, disciplineVan } from '../../utils/disciplines';

const DICHTHEID_SLEUTEL = 'dagstart-dichtheid';
const INDELING_SLEUTEL = 'dagstart-indeling';
const ALLE = '__alle__';

/** Hoeveel werkdagen er in beeld staan. Een werkweek is vijf dagen, vandaar 5/10/20. */
const BREEDTES = [
  { dagen: 5, label: '1w' },
  { dagen: 10, label: '2w' },
  { dagen: 20, label: '4w' },
];

/**
 * Het zichtbare venster: `breedte` werkdagen, rollend vanaf gisteren.
 *
 * Bewust géén kalenderweek meer. Op donderdag wil je in een dagstart vooruitkijken naar
 * volgende week, niet naar de twee dagen die er nog van deze week over zijn — en zeker
 * niet eerst een keer op "volgende week" hoeven klikken. Vandaag staat op plek twee met
 * gisteren ervóór, hetzelfde anker als het weekplan: de dagstart begint met wat er
 * gisteren is blijven liggen.
 */
/** Werkdagen van vandaag naar `doelISO` (negatief als de datum eerder ligt); weekend telt niet. */
export function werkdagenTussen(vandaagISO, doelISO) {
  const van = new Date(`${vandaagISO}T00:00:00`);
  const tot = new Date(`${doelISO}T00:00:00`);
  if (Number.isNaN(tot.getTime()) || van.getTime() === tot.getTime()) return 0;
  const richting = tot > van ? 1 : -1;
  let n = 0;
  const d = new Date(van);
  while (richting > 0 ? d < tot : d > tot) {
    d.setDate(d.getDate() + richting);
    if (d.getDay() !== 0 && d.getDay() !== 6) n += richting;
  }
  return n;
}

function werkdagVenster(vandaagISO, dagShift, breedte) {
  const uit = [];
  let dag = verschuifWerkdagen(vandaagISO, dagShift - 1);
  for (let i = 0; i < breedte; i += 1) {
    uit.push(dag);
    dag = verschuifWerkdagen(dag, 1);
  }
  return uit;
}

const kortDatum = (iso) => {
  const d = parseISODate(iso);
  return d ? d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }).replace('.', '') : iso;
};

export default function Dagstart() {
  const navigate = useNavigate();
  const { rules, reload: herlaadRegels } = useCapacityRules();
  const [regelsOpen, setRegelsOpen] = useState(false);

  const [dagShift, setDagShift] = useState(0);
  const [breedte, setBreedte] = useState(BREEDTES[0].dagen);
  const [persoon, setPersoon] = useState(null);
  // Sneltoets naar een collega: filtert de namenbalk terwijl je typt (26 aug).
  const [zoekNaam, setZoekNaam] = useState('');
  const [team, setTeam] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [dichtheid, setDichtheid] = useState(() => {
    const bewaard = localStorage.getItem(DICHTHEID_SLEUTEL);
    return DICHTHEDEN.includes(bewaard) ? bewaard : 'comfortabel';
  });
  const [gekozenTaak, setGekozenTaak] = useState(null);

  const [bulkVoor, setBulkVoor] = useState(null);
  // Urgentie (achterstallig/vandaag/later) of prioriteit als hoofdindeling. Uit de
  // wiki-feedback: op prioriteit lezen is voor sommige mensen duidelijker dan de
  // kleuren binnen een urgentie-lane. De keuze blijft staan per gebruiker.
  const [indeling, setIndeling] = useState(() => {
    const bewaard = localStorage.getItem(INDELING_SLEUTEL);
    return bewaard === 'prioriteit' ? 'prioriteit' : 'urgentie';
  });
  const kiesIndeling = (v) => {
    if (!v) return;
    setIndeling(v);
    try { localStorage.setItem(INDELING_SLEUTEL, v); } catch { /* private mode */ }
  };

  // De lanes worden afgeleid van de échte vandaag, niet van het zichtbare venster:
  // blader je naar volgende week, dan mag "achterstallig" niet iets anders betekenen.
  const vandaagISO = useMemo(() => formatDateISO(new Date()), []);

  const dagen = useMemo(
    () => werkdagVenster(vandaagISO, dagShift, breedte),
    [vandaagISO, dagShift, breedte]
  );
  const start = dagen[0] || vandaagISO;
  const eind = dagen[dagen.length - 1] || start;
  // Werkdagen ná het venster waarover de capaciteitsrij méérekent (niet getoond) — #62.
  const horizonDagen = useMemo(() => {
    const eersteNa = verschuifWerkdagen(eind, 1);
    const laatste = formatDateISO(addDays(new Date(`${eind}T12:00:00`), 61));
    return werkdagenInBereik(eersteNa, laatste);
  }, [eind]);

  // Waar ‹ en › naartoe gaan. De datalaag haalt die vensters stil op de achtergrond op,
  // zodat een stap van één werkdag uit de buffer komt in plaats van uit Airtable + Graph.
  const buren = useMemo(() => [-1, 1].map((verschuiving) => {
    const venster = werkdagVenster(vandaagISO, dagShift + verschuiving, breedte);
    return { start: venster[0], eind: venster[venster.length - 1] };
  }), [vandaagISO, dagShift, breedte]);

  // Hoeveel tijdlijnen er op dit moment een gebaar afhandelen. Bij de "Alle"-tab staan er
  // meerdere naast elkaar, vandaar een teller en geen boolean.
  const bezigRef = useRef(0);
  useKlantAfkortingen(); // klant-afkortingen (OMWB, KIWA) laden vóór de blokken renderen
  usePriveTaken(); // 🔒 privé-taken van collega's verbergen op dit (gedeelde) scherm
  const { instance: msal } = useMsal();
  const userEmail = msal.getActiveAccount()?.username || '';
  const meldBezig = useCallback((bezig) => {
    bezigRef.current = Math.max(0, bezigRef.current + (bezig ? 1 : -1));
  }, []);

  // De achtergrondronde mag alleen als er niets onder iemands handen kan verschuiven:
  // geen lopend sleep- of toetsgebaar, geen open detailpaneel, geen bulkdialoog.
  const magVernieuwen = useCallback(
    () => bezigRef.current === 0 && !gekozenTaak && !bulkVoor,
    [gekozenTaak, bulkVoor]
  );

  const {
    eersteLading, verversend, fout, personenFout, assignees, instelbaarVerborgen, takenPerPersoon, horizonPerPersoon,
    agendaUren, agendaFout, personen, ververs, werkTaakBij, verzetInBulk, maakOngedaan,
    zetLokaal,
  } = useDagstartData({ start, eind, buren, magVernieuwen });
  // Deep link vanuit de PL-todo's (/dagstart?taak=recXXX): paneel openen zodra de taak
  // in de geladen data staat. Eén keer; daarna mag de gebruiker gewoon sluiten.
  const [zoekParams, setZoekParams] = useSearchParams();
  const taakUitUrl = zoekParams.get('taak');
  useEffect(() => {
    if (!taakUitUrl) return;
    const alles = Object.values(takenPerPersoon || {}).flat();
    const t = alles.find((x) => x.id === taakUitUrl);
    if (t) {
      setGekozenTaak(t);
      setZoekParams((prev) => { const n = new URLSearchParams(prev); n.delete('taak'); return n; }, { replace: true });
    }
  }, [taakUitUrl, takenPerPersoon, setZoekParams]);
  // Personen-selectie: alleen voor admins en de planning (Joris, 1 sep).
  const { permissions } = useUserRole();
  const magPersonenKiezen = !!(permissions?.admin_settings?.view || permissions?.planner_todo?.edit);
  const [personenDlg, setPersonenDlg] = useState(null); // { verborgen:Set, busy }
  const openPersonenDlg = () => setPersonenDlg({
    verborgen: new Set(instelbaarVerborgen || []), busy: false,
  });
  const bewaarPersonenKeuze = async () => {
    setPersonenDlg((d) => ({ ...d, busy: true }));
    try {
      const r = await authFetch(`${AI_ENGINE_URL}/api/v1/instellingen/dagstart-personen`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verborgen: [...personenDlg.verborgen], updated_by: userEmail }),
      });
      if (!r.ok) throw new Error('Opslaan mislukt');
      setPersonenDlg(null);
      ververs();
      toast.success('Zichtbare personen bijgewerkt');
    } catch (e) {
      toast.error(e.message);
      setPersonenDlg((d) => (d ? { ...d, busy: false } : d));
    }
  };

  // Voornaam → e-mail (de dagstart denkt in voornamen, /Api/tasks in e-mail).
  const emailVoorNaam = useMemo(() => {
    const kaart = {};
    (personen || []).forEach((p) => {
      const naam = p.firstName || p.name;
      if (naam && p.email) kaart[naam] = p.email;
    });
    return kaart;
  }, [personen]);

  // ➕ Snel een taak toevoegen bij een persoon op een dag (wens 31 aug).
  const [nieuweTaak, setNieuweTaak] = useState(null); // { persoon, datum, naam, uren, prio, project, bezig }
  const [projectOpties, setProjectOpties] = useState([]);
  const projectZoekTimer = useRef(null);
  const zoekProjecten = (q) => {
    if (projectZoekTimer.current) clearTimeout(projectZoekTimer.current);
    if (!q || q.length < 2) { setProjectOpties([]); return; }
    projectZoekTimer.current = setTimeout(async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/projects?search=${encodeURIComponent(q)}`);
        const d = await leesJson(r, 'Projecten zoeken');
        setProjectOpties((d.projects || []).slice(0, 12).map((p) => ({
          id: p.id,
          label: `${p.fields?.Projectnummer ? `${p.fields.Projectnummer}. ` : ''}${p.fields?.Projectnaam || ''}`,
        })));
      } catch (e) { meldFout(e, 'Projecten zoeken'); }
    }, 300);
  };
  const bewaarNieuweTaak = async () => {
    const nt = nieuweTaak;
    if (!nt?.naam?.trim() || nt.bezig) return;
    setNieuweTaak((d) => ({ ...d, bezig: true }));
    try {
      const body = {
        NaamTaak: nt.naam.trim(),
        StartDateUitvoerder: nt.datum,
        EndDateUitvoerder: nt.datum,
        PrioNr: nt.prio || 'Geen prio',
        ...(nt.uren ? { UrenWerk: Number(nt.uren) } : {}),
        ...(nt.project?.id ? { Project: [nt.project.id] } : {}),
        ...(nt.email ? { assigneeEmail: nt.email } : {}),
      };
      const r = await authFetch(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Taak aanmaken mislukt');
      toast.success(`Taak voor ${nt.persoon} op ${nt.datum.slice(8, 10)}-${nt.datum.slice(5, 7)} aangemaakt`);
      setNieuweTaak(null);
      ververs();
    } catch (e) {
      toast.error(e.message);
      setNieuweTaak((d) => (d ? { ...d, bezig: false } : d));
    }
  };

  const teams = useMemo(
    () => [...new Set(assignees.map((a) => a.team).filter(Boolean))].sort(),
    [assignees]
  );

  // Discipline gaat vóór het (in de praktijk lege) team-veld: het team werkt in drie
  // groepen en wil de dagstart per groep doorlopen, niet langs twintig losse namen.
  const disciplinePerPersoon = useMemo(() => {
    const kaart = {};
    // Functie uit de personenlijst erbij: de dagstart-assignees zelf hebben er
    // geen, waardoor nieuwe collega's (Jade) altijd bij Productie belandden.
    const functiePerVoornaam = {};
    (personen || []).forEach((p) => {
      const vn = String(p.firstName || p.name || '').trim().split(/\s+/)[0];
      if (vn && !functiePerVoornaam[vn]) functiePerVoornaam[vn] = p.function || '';
    });
    assignees.forEach((a) => {
      kaart[a.naam] = disciplineVan({ name: a.naam, function: a.functie || a.function || functiePerVoornaam[a.naam] || '' });
    });
    return kaart;
  }, [assignees, personen]);

  const aantalPerDiscipline = useMemo(() => {
    const telling = {};
    assignees.forEach((a) => {
      const k = disciplinePerPersoon[a.naam];
      telling[k] = (telling[k] || 0) + 1;
    });
    return telling;
  }, [assignees, disciplinePerPersoon]);

  const zichtbaar = useMemo(() => {
    let lijst = team ? assignees.filter((a) => a.team === team) : assignees;
    if (discipline) lijst = lijst.filter((a) => disciplinePerPersoon[a.naam] === discipline);
    const z = zoekNaam.trim().toLowerCase();
    if (z) lijst = lijst.filter((a) => a.naam.toLowerCase().includes(z));
    return lijst;
  }, [assignees, team, discipline, disciplinePerPersoon, zoekNaam]);

  // Eerste persoon pas kiezen als de lijst er is; een useEffect met setState zou een
  // extra render kosten voor iets wat je gewoon kunt afleiden.
  const actief = persoon && (persoon === ALLE || zichtbaar.some((a) => a.naam === persoon))
    ? persoon
    : (zichtbaar[0]?.naam ?? null);

  const kiesDichtheid = (waarde) => {
    if (!waarde) return;
    setDichtheid(waarde);
    localStorage.setItem(DICHTHEID_SLEUTEL, waarde);
  };

  const verzetEen = useCallback((taak) => {
    const item = naarVandaag(taak, vandaagISO);
    const oudeStart = String(taak.startDate || '').slice(0, 10) || null;
    const oudEind = String(taak.endDate || '').slice(0, 10) || null;
    return werkTaakBij(taak.id, {
      StartDateUitvoerder: item.StartDateUitvoerder,
      EndDateUitvoerder: item.EndDateUitvoerder,
    }, {
      melding: `${taak.name} staat op vandaag`,
      // Zelfde terugweg als slepen (verzet): een losse taak per ongeluk naar vandaag halen
      // moet met één klik terug kunnen.
      terug: { StartDateUitvoerder: oudeStart, EndDateUitvoerder: oudEind },
    });
  }, [vandaagISO, werkTaakBij]);

  const vinkAf = useCallback((taak) => (
    // Beide velden in één actie — zie TaskDetailDrawer: "afgerond" bestaat twee keer.
    werkTaakBij(taak.id, { Afgerond: true, PrioNr: 'Afgerond' }, {
      melding: `${taak.name} afgerond`,
      // Het vinkje is klein en het blok verdwijnt meteen; zonder terugweg is een
      // misklik voor een meekijkende zaal een zoektocht in Airtable.
      terug: { Afgerond: false, PrioNr: taak.prio || 'Geen prio' },
    })
  ), [werkTaakBij]);

  /** Prioriteit wijzigen vanaf het blok zelf (rechtsklik), met ongedaan maken. */
  const wijzigPrio = useCallback((taak, prio) => {
    if (!taak || !prio || taak.prio === prio) return false;
    return werkTaakBij(taak.id, { PrioNr: prio }, {
      melding: `${taak.name} → ${prio}`,
      terug: { PrioNr: taak.prio || 'Geen prio' },
    });
  }, [werkTaakBij]);

  /** Uitkomst van een sleep- of resize-gebaar wegschrijven. */
  // Dupliceren: dezelfde taak nog een keer, op een (andere) dag. Vanuit het detailpaneel
  // (zelfde dagen) of met Alt/Shift + slepen (Joris, 14 sep). Afgerond/Opgestuurd gaat niet
  // mee — een kopie is nieuw werk. Ongedaan maken = de kopie weer verwijderen.
  const dupliceerTaak = useCallback(async (taak, { startDate, endDate } = {}) => {
    const start = startDate || String(taak.startDate || '').slice(0, 10);
    const eind = endDate || String(taak.endDate || '').slice(0, 10) || start;
    const prio = ['Afgerond', 'Opgestuurd'].includes(taak.prio) ? 'Geen prio' : (taak.prio || 'Geen prio');
    const email = emailVoorNaam[taak.assignee] || null;
    const body = {
      NaamTaak: taak.name,
      StartDateUitvoerder: start,
      EndDateUitvoerder: eind,
      PrioNr: prio,
      ...(taak.hours ? { UrenWerk: Number(taak.hours) } : {}),
      ...(taak.type ? { Type: taak.type } : {}),
      ...(taak.projectId ? { Project: [taak.projectId] } : {}),
      ...(taak.deliverableId ? { Deliverable: [taak.deliverableId] } : {}),
      ...(taak.deadline ? { Deadline: String(taak.deadline).slice(0, 10) } : {}),
      ...(taak.deadlineType ? { TypeDeadline: taak.deadlineType } : {}),
      ...(taak.opmerking ? { Opmerking: taak.opmerking } : {}),
      ...(email ? { assigneeEmail: email } : {}),
    };
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || d.detail || 'Dupliceren mislukt');
      const nieuwId = d.id || d.airtable_id || d.airtableId || d.task?.id || d.record?.id;
      ververs();
      biedOngedaanAan(`${taak.name} gedupliceerd naar ${kortDatum(start)}`, async () => {
        if (!nieuwId) throw new Error('id van de kopie onbekend');
        const rr = await authFetch(`${BACKEND_URL}/Api/tasks/${nieuwId}`, { method: 'DELETE' });
        if (!rr.ok) throw new Error('kopie verwijderen mislukt');
        ververs();
      });
      return d;
    } catch (e) {
      toast.error(e.message);
      return null;
    }
  }, [emailVoorNaam, ververs]);

  const verzet = useCallback(({ taak, soort, startDate, endDate, kopie }) => {
    if (kopie) return dupliceerTaak(taak, { startDate, endDate });
    const oudeStart = String(taak.startDate || '').slice(0, 10) || null;
    const oudEind = String(taak.endDate || '').slice(0, 10) || null;
    return werkTaakBij(taak.id, {
      StartDateUitvoerder: startDate,
      EndDateUitvoerder: endDate,
    }, {
      melding: soort === 'verplaats'
        ? `${taak.name} verzet naar ${kortDatum(startDate)}`
        : `${taak.name} loopt nu ${kortDatum(startDate)} – ${kortDatum(endDate)}`,
      terug: { StartDateUitvoerder: oudeStart, EndDateUitvoerder: oudEind },
    });
  }, [werkTaakBij, dupliceerTaak]);

  // Cmd+Z / Ctrl+Z. Niet als je in een tekstveld staat — daar hoort de browser zijn
  // eigen ongedaan-maken te doen (het detailpaneel heeft invoervelden).
  useEffect(() => {
    const opToets = (e) => {
      if (e.key !== 'z' && e.key !== 'Z') return;
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey) return;
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      e.preventDefault();
      maakOngedaan();
    };
    window.addEventListener('keydown', opToets);
    return () => window.removeEventListener('keydown', opToets);
  }, [maakOngedaan]);

  const tePonen = actief === ALLE ? zichtbaar : zichtbaar.filter((a) => a.naam === actief);

  // De taak in het paneel steeds vers uit de data halen in plaats van de kopie te tonen
  // van het moment waarop je klikte. Pas je in het paneel de uren of de deadline aan, dan
  // moet de rest van datzelfde paneel meebewegen — anders staat er na het opslaan van 4
  // uur nog steeds "geschat" onder.
  const detailTaak = useMemo(() => {
    if (!gekozenTaak) return null;
    const alles = Object.values(takenPerPersoon || {}).flat();
    return alles.find((t) => t.id === gekozenTaak.id) || gekozenTaak;
  }, [gekozenTaak, takenPerPersoon]);

  return (
    // `width: 100%` + `minWidth: 0` zijn hier geen opsmuk: App.js zet elke niet-full-bleed
    // pagina in een `Grid container justifyContent="center"`. Als flex-item krimpt deze Box
    // zonder die twee regels niet onder zijn min-content, dus duwt de brede tijdlijn de
    // pagina breder dan het scherm — en dan verdeelt `center` de overflow over links én
    // rechts, waardoor de linkerkolom onder de navigatie verdwijnt.
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: 1800, mx: 'auto', px: { xs: 1.5, sm: 2.5 }, py: { xs: 1.5, sm: 2 } }}>
      <PageHeader
        dense
        icon="wb_twilight"
        title="Dagstart"
        accent={{ bg: '#fef3c7', color: '#b45309' }}
        rightActions={(
          <>
            {/* De tooltip vatte de berekening samen in twee zinnen, maar juist in een
                dagstart is de vraag "waarom staat hij op 138%?" — en dan wil je de hele
                regelset kunnen lezen (en als planner meteen aanpassen). Zelfde paneel als
                op /capaciteit, dus de uitleg kan niet uit de pas lopen. */}
            <Tooltip title="Zo rekenen we de bezetting uit">
              <IconButton size="small" onClick={() => setRegelsOpen(true)} aria-label="Zo rekenen we de bezetting uit">
                <InfoOutlinedIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
              </IconButton>
            </Tooltip>
            {/* Presenteren is hier geen aparte modus maar de ruimste dichtheid: in een
                Teams-share worden 0,64rem-grijstinten onleesbaar. */}
            <ToggleButtonGroup size="small" exclusive value={dichtheid} onChange={(e, v) => kiesDichtheid(v)}>
              <ToggleButton value="comfortabel" sx={{ px: 1 }} aria-label="Comfortabel">
                <DensityMediumIcon sx={{ fontSize: 16 }} />
              </ToggleButton>
              <ToggleButton value="compact" sx={{ px: 1 }} aria-label="Compact">
                <DensitySmallIcon sx={{ fontSize: 16 }} />
              </ToggleButton>
              <Tooltip title="Presenteren — grotere letters voor een gedeeld scherm">
                <ToggleButton value="presentatie" sx={{ px: 1 }} aria-label="Presenteren">
                  <SlideshowIcon sx={{ fontSize: 16 }} />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
            {/* Zonder aanwijzing vindt niemand de pijltjes: een blok ziet er niet uit
                alsof het focus kan krijgen. */}
            <Tooltip
              arrow
              title={(
                <Box sx={{ whiteSpace: 'pre-line', fontSize: '0.72rem', lineHeight: 1.7 }}>
                  {[
                    'Tab — volgende taak',
                    '← →  een werkdag opschuiven',
                    'Shift + ← →  vijf werkdagen',
                    'Alt + ← →  einddatum (duur)',
                    'Alt + Shift + ← →  startdatum',
                    'T — naar vandaag   ·   Enter — details',
                    'Esc — beweging annuleren   ·   Cmd+Z — ongedaan',
                  ].join('\n')}
                </Box>
              )}
            >
              <IconButton size="small" aria-label="Sneltoetsen">
                <KeyboardOutlinedIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Opnieuw ophalen">
              <IconButton size="small" onClick={ververs}><RefreshIcon sx={{ fontSize: 18 }} /></IconButton>
            </Tooltip>
          </>
        )}
      />
      {/* Taken die al op Afgerond staan en daardoor onzichtbaar zijn (9 okt) — alleen als er zijn */}
      <AfgerondSignaal sx={{ mb: 1 }} />

      {/* Disciplinerij: het team loopt de dagstart per groep door (Animatie & Marketing,
          Camera & Edit, Productie). Staat bewust bóven de namen — je kiest eerst de groep
          en dan pas de persoon, net als in de weekoverzichten in Airtable. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
        {[{ key: '', label: 'Iedereen', aantal: assignees.length }, ...DISCIPLINES.map((d) => ({ ...d, aantal: aantalPerDiscipline[d.key] || 0 }))].map((d) => {
          const gekozen = discipline === d.key;
          return (
            <Box
              key={d.key || 'alle'}
              onClick={() => setDiscipline(d.key)}
              sx={{
                px: 1.25, py: 0.35, borderRadius: '9999px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 0.5, transition: 'all 0.15s',
                bgcolor: gekozen ? '#fef3c7' : '#f1f5f9',
                border: `1.5px solid ${gekozen ? '#f59e0b55' : 'transparent'}`,
                '&:hover': gekozen ? {} : { bgcolor: '#e2e8f0' },
              }}
            >
              <Typography sx={{ fontSize: '0.72rem', fontWeight: gekozen ? 700 : 500, color: gekozen ? '#b45309' : '#64748b' }}>
                {d.label}
              </Typography>
              <Typography component="span" sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8' }}>
                {d.aantal}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Eén balk: personen, teamfilter, periode. In Mijn Week waren dit drie
          losse lagen van samen ~180px, waarvan één regel debug-tekst. */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
        mb: 1, py: 0.25, borderBottom: '1px solid #e8edf2',
      }}>
        <TextField
          size="small"
          value={zoekNaam}
          onChange={(e) => setZoekNaam(e.target.value)}
          placeholder="Zoek naam…"
          onKeyDown={(e) => {
            // Enter = spring naar de eerste match en maak het veld weer leeg.
            if (e.key === 'Enter' && zichtbaar[0]) { setPersoon(zichtbaar[0].naam); setZoekNaam(''); }
            if (e.key === 'Escape') setZoekNaam('');
          }}
          sx={{ width: 132, '& .MuiInputBase-root': { height: 30, fontSize: '0.8rem' } }}
        />
        <Tabs
          value={actief ?? false}
          onChange={(e, v) => setPersoon(v)}
          variant="scrollable"
          scrollButtons="auto"
          // `minWidth: 0` is hier geen detail: zonder die regel houdt een flex-item zijn
          // volledige inhoudsbreedte aan, dus duwden twintig namen de balk — en daarmee de
          // hele pagina — breder dan het scherm. Dat was de horizontale schuifbalk waarvoor
          // je moest uitzoomen; de tabstrip scrolt nu zelf.
          sx={{ minHeight: 34, flex: 1, minWidth: 0, '& .MuiTab-root': { minHeight: 34, py: 0, px: 1.25, textTransform: 'none', fontSize: '0.8rem', fontWeight: 700 } }}
        >
          {zichtbaar.map((a) => (
            <Tab key={a.naam} value={a.naam} label={a.naam} />
          ))}
          {zichtbaar.length > 1 && <Tab value={ALLE} label={`Alle (${zichtbaar.length})`} />}
        </Tabs>

        {teams.length > 1 && (
          <Select
            size="small"
            displayEmpty
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            sx={{ fontSize: '0.75rem', height: 30 }}
          >
            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Alle teams</MenuItem>
            {teams.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: '0.8rem' }}>{t}</MenuItem>)}
          </Select>
        )}

        <ToggleButtonGroup size="small" exclusive value={breedte} onChange={(e, v) => v && setBreedte(v)}>
          {BREEDTES.map((b) => (
            <ToggleButton key={b.dagen} value={b.dagen} sx={{ px: 1, py: 0.25, fontSize: '0.72rem', fontWeight: 700 }}>
              {b.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Tooltip arrow title="Indeling van de rijen: op urgentie (wat is blijven liggen) of op prioriteit">
          <ToggleButtonGroup size="small" exclusive value={indeling} onChange={(e, v) => kiesIndeling(v)}>
            <ToggleButton value="urgentie" sx={{ px: 1, py: 0.25, fontSize: '0.72rem', fontWeight: 700 }}>
              Urgentie
            </ToggleButton>
            <ToggleButton value="prioriteit" sx={{ px: 1, py: 0.25, fontSize: '0.72rem', fontWeight: 700 }}>
              Prioriteit
            </ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>

        {magPersonenKiezen && (
          <Tooltip arrow title="Kies welke personen zichtbaar zijn in de dagstart (planner/admin)">
            <IconButton size="small" onClick={openPersonenDlg} sx={{ border: '1px solid #e2e8f0', borderRadius: 1 }}>
              <GroupIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Per wérkdag schuiven, niet per week: op donderdag wil je vooruit kunnen kijken
            zonder eerst een hele week over te slaan. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          <Tooltip title="Een werkdag terug" arrow>
            <IconButton size="small" onClick={() => setDagShift((s) => s - 1)} aria-label="Een werkdag terug">
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={dagShift === 0 ? '' : 'Terug naar vandaag'} arrow>
            <span>
              <Button
                size="small"
                onClick={() => setDagShift(0)}
                disabled={dagShift === 0}
                sx={{
                  textTransform: 'none', fontSize: '0.72rem', fontWeight: 700, py: 0,
                  minWidth: 118, color: '#2563eb', '&.Mui-disabled': { color: '#64748b' },
                }}
              >
                {kortDatum(start)} – {kortDatum(eind)}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Een werkdag vooruit" arrow>
            <IconButton size="small" onClick={() => setDagShift((s) => s + 1)} aria-label="Een werkdag vooruit">
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          {/* Losse knop: dat de datumknop (en toets T) terugspringt vond niemand (feedback #49). */}
          <Button size="small" variant="outlined" onClick={() => setDagShift(0)} disabled={dagShift === 0}
            sx={{ minWidth: 0, px: 1, py: 0, mx: 0.25, fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', lineHeight: 1.8 }}>
            Vandaag
          </Button>
          {/* Grotere sprongen (feedback Raijmond 9 sep: "een maand vooruit"): per week en per
              maand, in werkdagen omgerekend zodat het venster netjes op een werkdag begint. */}
          {[['-1m', 'Een maand terug', -1, 'maand'], ['-1w', 'Een week terug', -1, 'week'],
            ['+1w', 'Een week vooruit', 1, 'week'], ['+1m', 'Een maand vooruit', 1, 'maand']].map(([lbl, tip, r, eenheid]) => (
            <Tooltip key={lbl} title={tip} arrow>
              <Button size="small" onClick={() => {
                const d = new Date(`${start}T00:00:00`);
                if (eenheid === 'week') d.setDate(d.getDate() + 7 * r); else d.setMonth(d.getMonth() + r);
                setDagShift(werkdagenTussen(vandaagISO, formatDateISO(d)));
              }} sx={{ minWidth: 0, px: 0.6, py: 0, fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', color: '#64748b' }}>
                {lbl}
              </Button>
            </Tooltip>
          ))}
          {/* Direct naar een datum (een maand vooruit was dertig klikken — feedback #53). De
              datum wordt de eerste dag in beeld; de kiezer rekent terug naar werkdagen. */}
          <Tooltip title="Spring naar een datum" arrow>
            <input type="date" aria-label="Spring naar een datum" value={dagen?.[0] || ''}
              onChange={(e) => { const v = e.target.value; if (v) setDagShift(werkdagenTussen(vandaagISO, v)); }}
              style={{ marginLeft: 4, border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 6px', fontSize: 12, color: '#475569', width: 128, background: '#fff' }} />
          </Tooltip>
        </Box>
      </Box>

      {/* Dit scherm schrijft altijd naar de échte Airtable, ook vanaf test.fullframe.nl —
          Airtable is de enige bron. Zichtbaar maken zodat niemand hier "even oefent". */}
      <Chip
        size="small"
        label="Wijzigingen gaan direct naar de echte planning in Airtable"
        sx={{ mb: 1, height: 20, fontSize: '0.66rem', backgroundColor: '#fef3c7', color: '#92400e' }}
      />

      {fout && <Alert severity="error" sx={{ mb: 1.5 }}>{fout}</Alert>}
      {personenFout && (
        <Alert severity="warning" sx={{ mb: 1.5 }} action={<Button size="small" color="inherit" onClick={ververs} sx={{ textTransform: 'none', fontWeight: 700 }}>Opnieuw</Button>}>
          Personenlijst niet geladen — e-mail en functie per persoon kunnen ontbreken.
        </Alert>
      )}
      {/* Zonder agenda kloppen de percentages niet: afspraken tellen dan niet mee.
          Dat hoort het scherm te zeggen — een te laag getal zonder melding bleef in
          september drie dagen onopgemerkt. */}
      {agendaFout && (
        <Alert severity="warning" sx={{ mb: 1.5 }} action={<Button size="small" color="inherit" onClick={ververs} sx={{ textTransform: 'none', fontWeight: 700 }}>Opnieuw</Button>}>
          Agenda niet geladen — de percentages tellen nu alleen taken, geen afspraken.
        </Alert>
      )}

      {eersteLading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {/* Bij het bladeren blijft het vorige venster staan; alleen dit lijntje verraadt dat
          er nieuwe data onderweg is. Het scherm leegmaken en opnieuw opbouwen kostte niet
          alleen tijd, het las ook alsof de pagina crashte. */}
      <Box sx={{ height: 2, mb: 1 }}>
        {verversend && <LinearProgress sx={{ height: 2, borderRadius: 1 }} />}
      </Box>

      {!eersteLading && !fout && zichtbaar.length === 0 && (
        <Typography sx={{ py: 4, textAlign: 'center', color: '#94a3b8' }}>
          Geen medewerkers met taken in deze periode.
        </Typography>
      )}

      {!eersteLading && (
        <Box sx={{
          display: 'flex', flexDirection: 'column', gap: 1.25,
          // Airtable-stijl (Joris, 1 sep): één scrollcontainer voor horizontaal
          // (scrollbar onderaan i.p.v. dag-voor-dag klikken) én verticaal — alleen
          // zo blijven de sticky dagkoppen plakken terwijl je scrollt.
          overflow: 'auto', overscrollBehaviorX: 'contain',
          maxHeight: 'calc(100vh - 230px)',
        }}>
          {tePonen.map((a) => (
            <PersonTimeline
              key={a.naam}
              persoon={actief === ALLE ? a.naam : null}
              taken={(takenPerPersoon[a.naam] || []).filter((t) => !isVerborgenVoor(t.id, userEmail))}
              horizonTaken={(horizonPerPersoon?.[a.naam] || []).filter((t) => !isVerborgenVoor(t.id, userEmail))}
              horizonDagen={horizonDagen}
              dagen={dagen}
              agendaUren={agendaUren[a.naam] || {}}
              regels={rules}
              vandaagISO={vandaagISO}
              dichtheid={dichtheid}
              onTaakKlik={setGekozenTaak}
              onAfvinken={vinkAf}
              onNaarVandaag={verzetEen}
              onBulkNaarVandaag={(taken) => setBulkVoor(taken)}
              onVerzet={verzet}
              onNieuweTaak={(dag) => setNieuweTaak({
                persoon: a.naam, email: emailVoorNaam[a.naam] || null, datum: dag,
                naam: '', uren: '', prio: 'Geen prio', project: null, bezig: false,
              })}
              onBezig={meldBezig}
              indeling={indeling}
              onPrioWijzig={wijzigPrio}
            />
          ))}
        </Box>
      )}

      <TaskDetailDrawer
        taak={detailTaak}
        open={Boolean(gekozenTaak)}
        onClose={() => setGekozenTaak(null)}
        onWijzig={werkTaakBij}
        onVerwijderd={() => ververs()}
        onLokaal={zetLokaal}
        personen={personen}
        onNaarVandaag={verzetEen}
        onDupliceer={dupliceerTaak}
        onNaarProject={(id) => navigate(`/projects/${id}`)}
        regels={rules}
      />

      <Dialog open={regelsOpen} onClose={() => setRegelsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Capaciteitsregels</DialogTitle>
        <DialogContent dividers>
          <CapaciteitsRegels onSaved={herlaadRegels} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegelsOpen(false)} sx={{ textTransform: 'none' }}>Sluiten</Button>
        </DialogActions>
      </Dialog>

      {/* ➕ Snelle taak op persoon+dag */}
      <Dialog open={!!nieuweTaak} onClose={() => nieuweTaak?.bezig || setNieuweTaak(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          ➕ Taak voor {nieuweTaak?.persoon} · {nieuweTaak ? `${nieuweTaak.datum.slice(8, 10)}-${nieuweTaak.datum.slice(5, 7)}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {nieuweTaak && !emailVoorNaam[nieuweTaak.persoon] ? (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Geen e-mailadres bekend voor {nieuweTaak.persoon} — de taak komt dan op jouw naam.
            </Alert>
          ) : null}
          <TextField autoFocus fullWidth size="small" label="Taak" value={nieuweTaak?.naam || ''}
            onChange={(e) => setNieuweTaak((d) => ({ ...d, naam: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); bewaarNieuweTaak(); } }}
            sx={{ mb: 1.5, mt: 0.5 }} />
          <Autocomplete size="small" options={projectOpties} value={nieuweTaak?.project || null}
            filterOptions={(x) => x} isOptionEqualToValue={(o, v) => o.id === v.id}
            onInputChange={(_e, v, reason) => { if (reason === 'input') zoekProjecten(v); }}
            onChange={(_e, v) => setNieuweTaak((d) => ({ ...d, project: v }))}
            renderInput={(params) => <TextField {...params} label="Project (optioneel)" placeholder="Zoek op nummer of naam…" />}
            sx={{ mb: 1.5 }} />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField size="small" label="Uren" type="number" value={nieuweTaak?.uren || ''}
              onChange={(e) => setNieuweTaak((d) => ({ ...d, uren: e.target.value }))} sx={{ width: 100 }} />
            <TextField select size="small" label="Prio" value={nieuweTaak?.prio || 'Geen prio'}
              onChange={(e) => setNieuweTaak((d) => ({ ...d, prio: e.target.value }))} sx={{ flex: 1 }}>
              {['Prio 1', 'Prio 2', 'Prio 3', 'Prio 4', 'Geen prio', 'Deadline'].map((o) => (
                <MenuItem key={o} value={o}>{o}</MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNieuweTaak(null)} disabled={nieuweTaak?.bezig} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" onClick={bewaarNieuweTaak}
            disabled={nieuweTaak?.bezig || !nieuweTaak?.naam?.trim()}
            startIcon={nieuweTaak?.bezig ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            Taak aanmaken
          </Button>
        </DialogActions>
      </Dialog>

      {/* Personen-selectie (planner/admin): wie is zichtbaar als kolom */}
      <Dialog open={!!personenDlg} onClose={() => !personenDlg?.busy && setPersonenDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>👥 Zichtbare personen</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Uitgevinkt = niet in de dagstart (geldt voor iedereen). Alleen personen met taken in beeld staan in deze lijst.
          </Typography>
          {[...(assignees || []).map((a) => a.naam), ...(personenDlg ? [...personenDlg.verborgen] : [])]
            .filter((n, i, arr) => arr.indexOf(n) === i)
            .sort((a, b) => a.localeCompare(b))
            .map((naam) => {
              const uit = personenDlg?.verborgen?.has(naam);
              return (
                <Stack key={naam} direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
                  <Checkbox size="small" checked={!uit} sx={{ p: 0.5 }}
                    onChange={() => setPersonenDlg((d) => {
                      const v = new Set(d.verborgen);
                      if (v.has(naam)) v.delete(naam); else v.add(naam);
                      return { ...d, verborgen: v };
                    })} />
                  <Typography variant="body2" sx={{ opacity: uit ? 0.5 : 1 }}>{naam}</Typography>
                </Stack>
              );
            })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPersonenDlg(null)} disabled={personenDlg?.busy} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" onClick={bewaarPersonenKeuze} disabled={personenDlg?.busy}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            {personenDlg?.busy ? 'Bezig…' : 'Opslaan'}
          </Button>
        </DialogActions>
      </Dialog>

      <BulkNaarVandaagDialog
        open={Boolean(bulkVoor)}
        taken={bulkVoor || []}
        vandaagISO={vandaagISO}
        onSluit={() => setBulkVoor(null)}
        onBevestig={verzetInBulk}
      />
    </Box>
  );
}
